import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PageContentData } from '../../common/portalContracts';
import { EQueryKeys } from '../../constants/enums';
import useAxios from '../../hooks/useAxios';
import { EApiRoutes, fetchConfig, getApiRouteOrMock } from '../api.utils';

export const useContentManagement = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { contentId, portalId } = useParams();
  const customHeaders = portalId ? { 'x-portal': portalId } : undefined;

  const invalidatePortalTags = () => {
    queryClient.invalidateQueries([EQueryKeys.PORTAL_TAGS, portalId]);
  };

  const getPortalTags = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.PORTAL_TAGS, portalId],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.PORTAL_TAGS)}`,
        {},
        customHeaders
      );
    },
    enabled: !!portalId,
    staleTime: Infinity
  });

  const getCreatedContentsData = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.CONTENT_MANAGEMENT_GET_CONTENTS_DATA],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.CONTENT_MANAGEMENT)}`,
        {},
        customHeaders
      );
    }
  });

  const createPageContent = useMutation(
    async (contentData: PageContentData) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.CONTENT_MANAGEMENT)}`,
        contentData,
        customHeaders
      );
      return response.data;
    },
    {}
  );

  const getPageContentById = useQuery({
    ...fetchConfig.single,
    queryKey: [`${EQueryKeys.BADGES}_${contentId}`],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.CONTENT_MANAGEMENT)}/${contentId}`,
        {},
        customHeaders
      );
    },
    enabled: !!contentId
  });

  const editPageContent = useMutation(
    async (newContentData: PageContentData) => {
      const response = await axios.put(
        `${getApiRouteOrMock(EApiRoutes.CONTENT_MANAGEMENT)}/${contentId}`,
        newContentData,
        customHeaders
      );
      return response.data;
    },
    {}
  );

  const deletePageContent = useMutation(async (contentIdToDelete: string) => {
    return await axios.del(
      `${getApiRouteOrMock(EApiRoutes.CONTENT_MANAGEMENT)}/${contentIdToDelete}`,
      {},
      customHeaders
    );
  }, {});

  const createTag = useMutation(
    (name: string) => axios.post(`${getApiRouteOrMock(EApiRoutes.PORTAL_TAGS)}`, { name }, customHeaders),
    { onSuccess: invalidatePortalTags }
  );

  const updateTag = useMutation(
    ({ tagId, name }: { tagId: string; name: string }) =>
      axios.put(`${getApiRouteOrMock(EApiRoutes.PORTAL_TAGS)}/${tagId}`, { name }, customHeaders),
    { onSuccess: invalidatePortalTags }
  );

  const deleteTag = useMutation(
    (tagId: string) => axios.del(`${getApiRouteOrMock(EApiRoutes.PORTAL_TAGS)}/${tagId}`, {}, customHeaders),
    { onSuccess: invalidatePortalTags }
  );

  return {
    getCreatedContentsData,
    createPageContent,
    getPageContentById,
    editPageContent,
    deletePageContent,
    getPortalTags,
    createTag,
    updateTag,
    deleteTag
  };
};
